// R-162 — Continuous-feed client hook.
// Polls GET /queues/feed every 15s; exposes start/stop mutations.

import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api-client";
import type { GetNextResponse } from "../lib/queue-claim";

const POLL_INTERVAL_MS = 15_000;

export interface FeedLock {
  stepType: string;
  jobTypeId: string;
  startedAt: string;
}

interface FeedGetResponse {
  lock: FeedLock | null;
}

/**
 * E-051 — /queues/feed/start and /queues/next answer with the same claim
 * envelope (both routes serialize queueManager.getNextDocument the same way),
 * so this is an alias of the ONE client-side contract in lib/queue-claim.
 */
export type FeedStartResponse = GetNextResponse;

export function useFeedLock(): {
  lock: FeedLock | null;
  isLoading: boolean;
  error: string | null;
  start: (stepType: string, jobTypeId: string) => Promise<FeedStartResponse>;
  stop: () => Promise<void>;
  refetch: () => Promise<void>;
} {
  const [lock, setLock] = useState<FeedLock | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const refetch = useCallback(async () => {
    setError(null);
    try {
      const result = await api.get<FeedGetResponse>("/queues/feed");
      setLock(result.lock);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load feed lock");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void refetch();
    intervalRef.current = setInterval(() => void refetch(), POLL_INTERVAL_MS);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [refetch]);

  const start = useCallback(
    async (stepType: string, jobTypeId: string): Promise<FeedStartResponse> => {
      const result = await api.post<FeedStartResponse>("/queues/feed/start", {
        stepType,
        jobTypeId,
        connectionId: crypto.randomUUID(),
      });
      await refetch();
      return result;
    },
    [refetch],
  );

  const stop = useCallback(async (): Promise<void> => {
    await api.post<{ ok: true }>("/queues/feed/stop", {});
    setLock(null);
  }, []);

  return { lock, isLoading, error, start, stop, refetch };
}
